"use client";
import placeHolderImage from "@/assets/images/Info-img.jpg";
import { UploadFile, Maybe } from "@/types/generated";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { getNewDetailPreviewImage } from "@/utils";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { localCloudflareLoader } from "@/lib/utils";

interface NewsCardProps {
  title?: string;
  relatedHotelId: string;
  featureImage?: UploadFile;
  date: string;
  url: string;
  target?: string;
  imageAlt?: string;
  useFeaturedImage: Maybe<boolean> | undefined;
  headingLevel?: "h2" | "h3" | "h4" | "h5" | "h6";
}

const MAX_LINES = 5;
const LINE_HEIGHT_PX = 20;
/**
 * NewsCardItem Component
 *
 * Displays an individual news item card with an image, title, and date.
 * Dynamically adjusts text lines based on title height using ResizeObserver.
 * Falls back to a placeholder image if none is available.
 */
const NewsCardItem: React.FC<NewsCardProps> = ({
  title,
  relatedHotelId,
  featureImage,
  date,
  url,
  target,
  imageAlt = "image",
  useFeaturedImage,
  headingLevel: HeadingTag = "h3",
}) => {
  const { japanAreas } = useJapanAreasStore();
  const listHotel = japanAreas?.flatMap((area) => area.hotels) || [];

  /* Determine which image to display based on conditions */
  const NewsImageUrl =
    (useFeaturedImage
      ? featureImage || getNewDetailPreviewImage(relatedHotelId, listHotel)
      : getNewDetailPreviewImage(relatedHotelId, listHotel)) || undefined;

  const titleRef = useRef<HTMLHeadingElement>(null);

  const [descLines, setDescLines] = useState(3);
  if (descLines === -1) {
    /*no-op */
  }
  /* Dynamically calculate remaining lines for description 
     based on the rendered height of the title */
  useEffect(() => {
    if (!titleRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      const titleHeight = titleRef.current?.clientHeight || 0;
      const titleLines = Math.round(titleHeight / LINE_HEIGHT_PX);
      const remaining = Math.max(0, MAX_LINES - titleLines);
      setDescLines(remaining);
    });

    resizeObserver.observe(titleRef.current);
    return () => resizeObserver.disconnect();
  }, [title]);

  return (
    <a
      href={url}
      target={target}
      className="flex w-full max-w-full overflow-hidden"
    >
      <div className="relative aspect-square h-[140px] w-[140px] flex-shrink-0 sm:w-[164px] md:h-[172px] md:w-[265px] lg:aspect-[unset] lg:h-[174px] lg:w-[282px]">
        <Image
          src={
            NewsImageUrl?.cloudflare_url ||
            NewsImageUrl?.url ||
            placeHolderImage
          }
          aria-label={NewsImageUrl?.caption || ""}
          alt={NewsImageUrl?.alternativeText || imageAlt}
          fill
          className="rounded-xl object-cover lg:rounded-lg"
          loader={localCloudflareLoader(320)}
        />
      </div>
      <div className="flex flex-col justify-between overflow-hidden pt-1 pl-2 sm:p-4 sm:pt-1">
        <HeadingTag
          ref={titleRef}
          className="mb-1 overflow-hidden text-sm font-[var(--font-bold)] break-words text-ellipsis text-gray-800 md:text-base"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: MAX_LINES,
            WebkitBoxOrient: "vertical",
            lineHeight: `${LINE_HEIGHT_PX}px`,
          }}
        >
          {title}
        </HeadingTag>
        <p className="mt-2 text-xs text-[var(--color-neutral-gray)] md:mt-4 md:text-base">
          {date}
        </p>
      </div>
    </a>
  );
};

export default NewsCardItem;
